/**
 * The side drawer (docs/04 §3g): a bottom sheet on a phone and a right-hand
 * panel from 640 up, opened by the control that names it.
 *
 * Extracted when the second caller arrived, as the notification drawer's own
 * header said it should be: the bell's drawer was first, the Dev-only QA panel
 * (L-36) is second. Radix supplies the focus trap, Escape and overlay close,
 * and returns focus to the trigger on close — which is why the trigger is
 * passed IN and rendered inside the root rather than living beside it.
 */

import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { Button } from './Button.js';

export interface DrawerProps {
  /** The button that opens it; it gets focus back when the drawer closes. */
  readonly trigger: ReactElement;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The drawer's heading and accessible name. */
  readonly title: string;
  /** The close button's accessible name, e.g. "Close notifications". */
  readonly closeLabel: string;
  readonly children: ReactNode;
  /** A fixed bar under the scrolling body. */
  readonly footer?: ReactNode;
}

export function Drawer({
  trigger,
  open,
  onOpenChange,
  title,
  closeLabel,
  children,
  footer,
}: DrawerProps): ReactNode {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-brand-950/40" />
        {/* A bottom sheet on a phone and a right-hand panel from 640 up — the
            same two shapes the More menu and the list filters already use, so
            the app has one idea of what an overlay does rather than three. */}
        <RadixDialog.Content
          aria-describedby={undefined}
          className={
            'fixed inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-dialog bg-card shadow-lg ' +
            'pb-[env(safe-area-inset-bottom)] ' +
            'sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-[26rem] sm:rounded-none sm:pb-0'
          }
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-hairline px-4 py-3">
            <RadixDialog.Title className="font-display text-h2 text-ink">{title}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <Button variant="ghost" aria-label={closeLabel}>
                <X size={18} aria-hidden />
              </Button>
            </RadixDialog.Close>
          </div>
          <div className="min-h-0 grow overflow-y-auto px-4 py-2">{children}</div>
          {footer === undefined ? null : (
            <div className="shrink-0 border-t border-hairline px-4 py-3">{footer}</div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
